import axios from 'axios';
import { addDeviceIdToRequest } from '../utils/deviceId';
import { getApiUrl } from './apiConfig';

// Create axios instance with base URL
const baseURL = getApiUrl();

// Create toast function for error handling
let toastFunction = null;
export const setToastFunction = (toast) => {
  toastFunction = toast;
};

const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Accept': 'application/json'
  },
  withCredentials: true
});

// ⭐ Tracking retry count to avoid infinite loops
const retryConfig = new Map();

// ✅ Custom Retry Logic للـ Network Errors و 5xx Errors
const shouldRetry = (error, attempts = 0) => {
  // Max 3 retries
  if (attempts >= 3) return false;
  
  // Retry on network errors
  if (!error.response) return true;
  
  // Retry on specific status codes (500, 502, 503, 408)
  const retryableStatusCodes = [408, 500, 502, 503, 429];
  return retryableStatusCodes.includes(error.response.status);
};

// ✅ Exponential Backoff حساب التأخير
const getRetryDelay = (attempts) => {
  return Math.min(1000 * Math.pow(2, attempts), 10000) + Math.random() * 1000;
};

// ✅ Token Refresh Logic
const refreshAccessToken = async () => {
  try {
    const response = await axios.post(`${baseURL}/auth/refresh-token`, {}, {
      withCredentials: true
    });
    
    if (response.data?.token) {
      localStorage.setItem('token', response.data.token);
      return response.data.token;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Failed to refresh token:', error.message);
    return null;
  }
};

// Add request interceptor to include auth token and device ID
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // ✅ Remove Authorization header if no token
      delete config.headers.Authorization;
    }
    
    // Always send device ID (required by new session manager)
    addDeviceIdToRequest(config);
    
    // Initialize retry count for this request
    if (!retryConfig.has(config.url)) {
      retryConfig.set(config.url, 0);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors and caching
api.interceptors.response.use(
  (response) => {
    // Handle 304 Not Modified responses
    if (response.status === 304) {
      return response;
    }
    
    // Reset retry count on successful response
    retryConfig.delete(response.config.url);
    
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // Handle cancelled requests - ignore without showing toast or logging
    // Cancelled requests are normal when:
    // - Component unmounts during a request
    // - Query dependencies change and React Query aborts previous request
    // - User navigates away from page
    if (error.code === 'ERR_CANCELED') {
      // Clean up retry counter for cancelled requests
      if (config?.url) {
        retryConfig.delete(config.url);
      }
      // Silently ignore - this is expected behavior
      return Promise.reject(error);
    }
    
    // ✅ تطبيق Retry Logic مع Exponential Backoff
    if (shouldRetry(error, retryConfig.get(config.url) || 0) && config) {
      const attempts = (retryConfig.get(config.url) || 0) + 1;
      retryConfig.set(config.url, attempts);
      
      const delay = getRetryDelay(attempts);
      
      // 📢 Show notification on 503 and other temporary errors
      if ([503, 502, 500, 429].includes(error.response?.status)) {
        console.warn(`⚠️ Server error ${error.response.status} (attempt ${attempts}/3), retrying in ${delay}ms...`);
        
        if (toastFunction && attempts === 1) {
          // Only show on first retry to avoid spamming
          toastFunction(`الخادم مشغول حالياً. إعادة المحاولة...`, 'warning');
        }
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return api(config); // إعادة الطلب
    }
    
    // Handle network errors specifically for mobile
    if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
      console.error('📱 Mobile network error detected:', error.message);
      
      if (toastFunction) {
        toastFunction('فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.', 'error');
      }
      
      return Promise.reject(error);
    }
    
    // ✅ تحسين Token Refresh Logic
    if (error.response?.status === 401) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.error;
      const requestUrl = error.config?.url;
      
      // Don't auto-logout for password change failures
      if (requestUrl?.includes('/auth/password')) {
        return Promise.reject(error);
      }
      
      // Don't auto-logout for login failures (user is already on login page)
      if (requestUrl?.includes('/auth/login')) {
        return Promise.reject(error);
      }
      
      // Don't retry refresh token request itself
      if (requestUrl?.includes('/auth/refresh-token')) {
        // محاولة تحديث التوكن فشلت، تخرج المستخدم
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (toastFunction) {
          toastFunction('انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى.', 'warning');
        }
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return Promise.reject(error);
      }
      
      // ✅ محاولة تحديث التوكن قبل تسجيل الخروج
      if (!config._retry) {
        config._retry = true;
        
        try {
          const newToken = await refreshAccessToken();
          
          if (newToken) {
            // تم تحديث التوكن بنجاح، إعادة الطلب الأصلي
            config.headers.Authorization = `Bearer ${newToken}`;
            return api(config);
          } else {
            // فشل التحديث
            throw new Error('Token refresh failed');
          }
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError.message);
          // الخروج الفعلي
          handleSessionExpiration(errorData, errorMessage);
          return Promise.reject(error);
        }
      }
      
      // Otherwise continue with session expiration handling
      handleSessionExpiration(errorData, errorMessage);
    }
    
    // Handle 409 conflict (active session exists during login)
    if (error.response?.status === 409) {
      const errorData = error.response?.data;
      if (errorData?.code === 'ACTIVE_SESSION_EXISTS') {
        // This will be handled by the login component
        // Don't redirect here
        return Promise.reject(error);
      }
    }
    
    // ✅ Handle 503 Service Unavailable (after all retries failed)
    if (error.response?.status === 503) {
      console.error('❌ Server unavailable (503) - all retries exhausted');
      
      if (toastFunction) {
        toastFunction('الخادم غير متاح حالياً. يرجى محاولة إعادة الصفحة بعد قليل.', 'error');
      }
      
      return Promise.reject(error);
    }
    
    // Handle other server errors (500, 502, etc.)
    if (error.response?.status >= 500) {
      console.error(`Server error ${error.response.status}:`, error.response.data);
      
      // Don't spam toast - only show if no toast was shown during retries
      if (toastFunction) {
        toastFunction(`خطأ في الخادم (${error.response.status}). نحن نعمل على الحل.`, 'error');
      }
      
      return Promise.reject(error);
    }
    
    // ✅ Final cleanup: remove retry counter for this request
    if (config?.url) {
      retryConfig.delete(config.url);
    }
    
    return Promise.reject(error);
  }
);

// ✅ Helper function: معالجة انتهاء الجلسة
const handleSessionExpiration = (errorData, errorMessage) => {
  // Handle different session error types
  if (errorData?.shouldLogout || errorMessage?.includes('جهاز آخر')) {
    // Session conflict - show message and logout
    if (toastFunction) {
      toastFunction('تم تسجيل الدخول من جهاز آخر. سيتم تسجيل خروجك تلقائياً.', 'error');
    }
    
    // Clear storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Redirect after a short delay to allow toast to show
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
    
  } else if (errorMessage?.includes('انتهت صلاحية الجلسة')) {
    // Session expired
    if (toastFunction) {
      toastFunction('انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى.', 'warning');
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
  } else {
    // Generic 401 error
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }
};

export default api;

